import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import ReactDOM from 'react-dom/client';
import {
  Card,
  Table,
  Button,
  Tag,
  App,
  Space,
  Descriptions,
  Popconfirm,
  Modal,
  InputNumber,
  Input,
  Form,
  Collapse,
  Spin,
  Typography,
  Alert,
  Select,
} from 'antd';
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
  LinkOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import QRCode from 'qrcode.react';
import complianceService, { type InvoiceExchangeTransmission } from '@/services/compliance.service';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { firstNonUuidDisplay } from '@/utils/displayLabels';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { chartOfAccountsService } from '@/services/salesInvoice.service';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format/money';
import {
  supplierInvoiceService,
  type SupplierInvoiceDTO,
  type SupplierInvoiceLineDTO,
  type OpenDebitNoteRow,
  type ApplyDebitNoteAllocationDTO,
  type OpenAdvanceRow,
  type ApplyAdvanceAllocationDTO,
} from '@/services/supplierInvoice.service';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { BudgetExceedanceRequestModal } from '@/components/project/BudgetExceedanceRequestModal';
import { BudgetCodeLabel } from '@/components/project/BudgetCodeLabel';
import { projectService } from '@/services/project.service';
import { PrintablePurchaseBill } from '@/components/procurement/PrintablePurchaseBill';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { PrintableVoucher } from '@/components/sales-invoice/PrintableVoucher';
import {
  getInvoicePoOverheads,
  isSupplierInvoiceOverheadLine,
  sumSupplierInvoiceOverheadAmount,
} from '@/utils/supplierInvoiceOverhead';
import { useSystemSettings } from '@/hooks/useSettings';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { ENTITY_TYPE_SUPPLIER_INVOICE } from '@/constants/approval-entity-types';
import { headerProjectLabel, projectLabel } from '@/utils/projectDisplay';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import { DOCUMENT_ENTITY_TYPE_SUPPLIER_INVOICE } from '@/constants/document-entity-types';
import { useAuthStore } from '@/store/authStore';
import type { ThreeWayMatchResultDTO } from '@/services/supplierInvoice.service';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

const matchColors: Record<string, string> = {
  Pending: 'gold',
  Matched: 'green',
  Variance: 'orange',
  Hold: 'red',
  NotApplicable: 'default',
};

const SupplierInvoiceDetailPage: React.FC = () => {
  const { t } = useTranslation('procurement');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const statusLabel = (s: string) => ({
    Draft: t('statusDraft'),
    Submitted: t('statusSubmitted'),
    Approved: t('statusApproved'),
    Posted: t('statusPosted'),
    Cancelled: t('statusCancelled'),
  } as Record<string, string>)[s] ?? s;
  const matchStatusLabel = (v?: string | null) => ({
    Pending: t('statusPending'),
    Matched: t('statusMatched'),
    Variance: t('statusVariance'),
    Hold: t('statusHold'),
    NotApplicable: t('statusNotApplicable'),
  } as Record<string, string>)[v || 'Pending'] ?? v ?? t('statusPending');
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canMatch = Boolean(user?.is_super_admin || user?.permissions?.includes('procurement.invoice.match'));
  const canHold = Boolean(user?.is_super_admin || user?.permissions?.includes('procurement.invoice.hold'));
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [matchNotes, setMatchNotes] = useState('');
  const [forceMatchOpen, setForceMatchOpen] = useState(false);
  const [exceedanceOpen, setExceedanceOpen] = useState(false);
  const [exceedanceCheck, setExceedanceCheck] = useState<Record<string, unknown> | null>(null);
  const [exceedanceProjectId, setExceedanceProjectId] = useState('');
  const [exceedanceLines, setExceedanceLines] = useState<Array<Record<string, unknown>>>([]);
  const [discountPostOpen, setDiscountPostOpen] = useState(false);
  const [discountPostAccountId, setDiscountPostAccountId] = useState<string | undefined>();

  const { data: settings } = useSystemSettings();
  const [applyDnModalOpen, setApplyDnModalOpen] = useState(false);
  const [applyDnSubmitting, setApplyDnSubmitting] = useState(false);
  const [applyAdvanceModalOpen, setApplyAdvanceModalOpen] = useState(false);
  const [applyAdvanceSubmitting, setApplyAdvanceSubmitting] = useState(false);
  const [uaeEinvoiceLoading, setUaeEinvoiceLoading] = useState(false);
  const [uaeQrModalVisible, setUaeQrModalVisible] = useState(false);
  const { isCriticalCode } = useCriticalItemLookup();
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: invoice, isLoading } = useQuery({
    queryKey: ['supplier-invoice', id],
    queryFn: () => supplierInvoiceService.getById(id!),
    enabled: !!id,
  });
  const { data: matchResult, refetch: refetchMatch } = useQuery({
    queryKey: ['supplier-invoice-match', id],
    queryFn: () => supplierInvoiceService.getMatchResult(id!),
    enabled: !!id && !!invoice && invoice.invoice_status !== 'Cancelled',
  });
  const { data: allocations = [] } = useQuery({
    queryKey: ['supplier-invoice-debit-note-allocations', id],
    queryFn: () => supplierInvoiceService.getDebitNoteAllocations(id!),
    enabled: !!id && !!invoice && invoice.invoice_status !== 'Cancelled',
  });
  const { data: openDebitNotes = [] } = useQuery({
    queryKey: ['open-debit-notes', invoice?.supplier_id],
    queryFn: () => supplierInvoiceService.getOpenDebitNotes(invoice!.supplier_id),
    enabled: applyDnModalOpen && !!invoice?.supplier_id && invoice.invoice_status !== 'Cancelled',
  });
  const { data: advanceAllocations = [] } = useQuery({
    queryKey: ['supplier-invoice-advance-allocations', id],
    queryFn: () => supplierInvoiceService.getAdvanceAllocations(id!),
    enabled: !!id && !!invoice && invoice.invoice_status !== 'Cancelled',
  });
  const isGlPosted = !!invoice?.gl_posted;
  const { data: openAdvances = [] } = useQuery({
    queryKey: ['open-advances', invoice?.supplier_id, isGlPosted ? invoice?.po_id : null],
    queryFn: () =>
      supplierInvoiceService.getOpenAdvances(invoice!.supplier_id, isGlPosted ? invoice?.po_id : undefined),
    enabled: !!invoice?.supplier_id && invoice.invoice_status !== 'Cancelled',
  });
  const { data: plannedAdvances = [] } = useQuery({
    queryKey: ['supplier-invoice-planned-advances', id],
    queryFn: () => supplierInvoiceService.getPlannedAdvances(id!),
    enabled: !!id && !!invoice && !isGlPosted && invoice.invoice_status !== 'Cancelled',
  });

  const { data: uaeTransmissions = [], refetch: refetchUaeTransmissions } = useQuery({
    queryKey: ['uae-einvoice-transmissions', 'SupplierInvoice', id],
    queryFn: () =>
      id
        ? complianceService.listTransmissions({
            document_type: 'SupplierInvoice',
            document_id: id,
          })
        : Promise.resolve([] as InvoiceExchangeTransmission[]),
    enabled: !!id && !!invoice?.gl_posted,
  });

  const latestUaeTransmission = useMemo(
    () =>
      [...uaeTransmissions].sort(
        (a, b) => new Date(b.request_at).getTime() - new Date(a.request_at).getTime()
      )[0],
    [uaeTransmissions]
  );

  const uaeQrPayload = latestUaeTransmission?.qr_payload ?? '';

  const needsDiscountAccount =
    !!invoice &&
    invoice.invoice_status === 'Approved' &&
    invoice.invoice_type !== 'ServiceReceiptMatched' &&
    Number(invoice.discount_amount ?? 0) > 0 &&
    !invoice.discount_account_id;
  const { data: coaList = [] } = useQuery({
    queryKey: ['chart-of-accounts', 'all'],
    queryFn: () => chartOfAccountsService.getAll(),
    enabled: needsDiscountAccount,
  });
  const discountAccountOptions = useMemo(
    () =>
      (Array.isArray(coaList) ? coaList : [])
        .filter((a: any) => !a.is_group)
        .map((a: any) => ({ value: a.id as string, label: `${a.account_code} – ${a.account_name}` })),
    [coaList]
  );

  const advancePromptedRef = React.useRef(false);
  React.useEffect(() => {
    if (advancePromptedRef.current || !invoice || invoice.invoice_status !== 'Posted') return;
    supplierInvoiceService.getOpenAdvances(invoice.supplier_id, invoice.po_id).then((rows) => {
      const hasOpen = rows.some((r) => Number(r.open_balance) > 0);
      if (hasOpen) {
        advancePromptedRef.current = true;
        setApplyAdvanceModalOpen(true);
      }
    });
  }, [invoice?.id, invoice?.invoice_status]);

  const openExceedanceFromInvoice = async () => {
    if (!invoice || !id) return;
    const linesRaw = (invoice.lines || []) as any[];
    const projectId =
      String((invoice as any).project_id || '') ||
      String(linesRaw.find((l) => l.project_id)?.project_id || '');
    if (!projectId) {
      message.warning(t('invoiceNeedsProjectForExceedance'));
      return;
    }
    const lines = linesRaw.map((ln) => ({
      projectId: ln.project_id || projectId,
      wbsNodeId: ln.wbs_node_id || null,
      cbsCodeId: ln.cbs_code_id || null,
      costCategory: ln.cost_category || 'Material',
      amount: Number(ln.amount || ln.line_total || 0),
      sourceLineId: ln.id,
    }));
    try {
      const check = await projectService.checkCostControl(projectId, {
        lines,
        excludeSourceType: 'supplier_invoice',
        excludeSourceId: id,
      });
      setExceedanceProjectId(projectId);
      setExceedanceLines(lines);
      setExceedanceCheck(check);
      setExceedanceOpen(true);
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('couldNotCheckBudget'));
    }
  };

  const runAction = async (key: string, fn: () => Promise<unknown>) => {
    setActionLoading(key);
    try {
      await fn();
      message.success(tc('done'));
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice-advance-allocations', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice-planned-advances', id] });
      queryClient.invalidateQueries({ queryKey: ['open-advances'] });
    } catch (e: any) {
      const code = e?.response?.data?.errors?.code || e?.response?.data?.code;
      const msg = e?.response?.data?.message || tc('actionFailed');
      if (key === 'submit' && (code === 'BUDGET_EXCEEDANCE_REQUIRED' || /exceed|budget/i.test(String(msg)))) {
        message.warning(msg);
        await openExceedanceFromInvoice();
      } else {
        message.error(msg);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const copySupplierPortalLink = async () => {
    if (!id) return;
    setActionLoading('portal-link');
    try {
      const { token } = await supplierInvoiceService.issuePortalToken(id);
      const url = `${window.location.origin}/sales/invoices/supplier/${token}`;
      await navigator.clipboard.writeText(url);
      message.success(t('supplierPortalLinkCopied'));
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('couldNotCreatePortalLink'));
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrint = () => {
    if (!invoice) return;
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(<PrintablePurchaseBill invoice={invoice} onClose={cleanup} />);
    window.addEventListener('afterprint', cleanup);
  };

  const netPayableBeforeDn = invoice
    ? Math.max(0, Number(invoice.grand_total ?? 0) - Number(invoice.paid_amount ?? 0) - Number(invoice.debit_note_adjusted_amount ?? 0))
    : 0;
  const netPayable = invoice
    ? Math.max(
        0,
        Number(invoice.grand_total ?? 0) -
          Number(invoice.paid_amount ?? 0) -
          Number(invoice.debit_note_adjusted_amount ?? 0) -
          Number(invoice.advance_adjusted_amount ?? 0)
      )
    : 0;

  const handleApplyAdvances = async (values: { amounts: Record<string, number> }) => {
    if (!id || !values.amounts) return;
    const allocs: ApplyAdvanceAllocationDTO[] = Object.entries(values.amounts)
      .filter(([, amt]) => Number(amt) > 0)
      .map(([payment_voucher_id, amount]) => ({ payment_voucher_id, amount: Number(amount) }));
    if (!isGlPosted) {
      setApplyAdvanceSubmitting(true);
      try {
        await supplierInvoiceService.setPlannedAdvances(id, allocs);
        message.success(allocs.length ? t('advancesAppliedOnPost') : t('plannedAdvancesCleared'));
        setApplyAdvanceModalOpen(false);
        queryClient.invalidateQueries({ queryKey: ['supplier-invoice-planned-advances', id] });
      } catch (e: any) {
        message.error(e?.response?.data?.message || t('failedToSaveSupplierAdvances'));
      } finally {
        setApplyAdvanceSubmitting(false);
      }
      return;
    }
    if (allocs.length === 0) return;
    setApplyAdvanceSubmitting(true);
    try {
      await supplierInvoiceService.applyAdvances(id, allocs);
      message.success(t('supplierAdvancesApplied'));
      setApplyAdvanceModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice-advance-allocations', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice-debit-note-allocations', id] });
      queryClient.invalidateQueries({ queryKey: ['open-advances', invoice?.supplier_id] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('failedToApplySupplierAdvances'));
    } finally {
      setApplyAdvanceSubmitting(false);
    }
  };

  const handleApplyDebitNotes = async (values: { amounts: Record<string, number> }) => {
    if (!id || !values.amounts) return;
    const allocs: ApplyDebitNoteAllocationDTO[] = Object.entries(values.amounts)
      .filter(([, amt]) => Number(amt) > 0)
      .map(([supplier_debit_note_id, amount]) => ({ supplier_debit_note_id, amount: Number(amount) }));
    if (allocs.length === 0) return;
    setApplyDnSubmitting(true);
    try {
      await supplierInvoiceService.applyDebitNotes(id, allocs);
      message.success(t('debitNotesApplied'));
      setApplyDnModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-invoice-debit-note-allocations', id] });
      queryClient.invalidateQueries({ queryKey: ['open-debit-notes', invoice?.supplier_id] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('failedToApplyDebitNotes'));
    } finally {
      setApplyDnSubmitting(false);
    }
  };

  const handlePrintVoucher = async () => {
    if (!id) return;
    try {
      const voucher = await supplierInvoiceService.getVoucherBySupplierInvoiceId(id);
      if (!voucher) {
        message.warning(t('noVoucherFound'));
        return;
      }
      const companySettings = settings?.filter((s: any) => s.category === 'company') || [];
      const companyName = companySettings.find((s: any) => s.setting_key === 'company_name')?.setting_value ?? '';
      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.display = 'block';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(
        <PrintableVoucher
          voucher={voucher}
          referenceLabel={t('purchaseInvoice')}
          companyName={companyName}
          onClose={cleanup}
        />
      );
      window.addEventListener('afterprint', cleanup);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || t('failedToLoadVoucher'));
    }
  };

  const breadcrumbSegments = useMemo(
    () =>
      invoice
        ? [
            { title: t('supplyChain'), path: '/scm/dashboard' },
            { title: t('supplierInvoices'), path: '/scm/procurement/supplier-invoices' },
            { title: invoice.invoice_number || t('bill') },
          ]
        : null,
    [invoice?.id, invoice?.invoice_number, t]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const siviWorkflowStages = useMemo(() => {
    if (!invoice) return [];
    const st = invoice.invoice_status;
    const cancelled = st === 'Cancelled';
    return [
      {
        title: t('statusDraft'),
        description: t('workflowEntry'),
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('statusSubmitted'),
        description: t('workflowPendingApproval'),
        status: (cancelled ? 'wait' : st === 'Submitted' ? 'process' : ['Approved', 'Posted'].includes(st) ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('statusApproved'),
        description: t('workflowReadyToPost'),
        status: (cancelled ? 'wait' : st === 'Approved' ? 'process' : st === 'Posted' ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('statusPosted'),
        description: t('workflowGl'),
        status: (cancelled ? 'wait' : st === 'Posted' ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [invoice, t]);

  const siviWorkflowCurrent = useMemo(() => {
    if (!invoice || invoice.invoice_status === 'Cancelled') return 0;
    const st = invoice.invoice_status;
    if (st === 'Draft') return 0;
    if (st === 'Submitted') return 1;
    if (st === 'Approved') return 2;
    if (st === 'Posted') return 3;
    return 0;
  }, [invoice]);

  const totalAlignment = useMemo(() => {
    const lines = invoice?.lines || [];
    const lineNet = Math.round(lines.reduce((s, l) => s + Number(l.amount || 0), 0) * 100) / 100;
    const vat = Math.round(Number(invoice?.vat_amount || 0) * 100) / 100;
    const discount = Math.round(Number(invoice?.discount_amount || 0) * 100) / 100;
    const expected = Math.round((lineNet - discount + vat) * 100) / 100;
    const stored = Math.round(Number(invoice?.grand_total || 0) * 100) / 100;
    const diff = Math.round((stored - expected) * 100) / 100;
    return { lineNet, vat, discount, expected, stored, diff };
  }, [invoice]);

  if (!id) {
    navigate('/scm/procurement/supplier-invoices');
    return null;
  }

  if (isLoading && !invoice) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      {id ? <AiPageContextSync module="procurement" docType="SupplierInvoice" docId={id} /> : null}
        <Spin size="large" />
      </div>
    );
  }
  if (!invoice) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <p>{t('purchaseBillNotFound')}</p>
        </Card>
      </div>
    );
  }

  const showUniversalApproval = !!invoice.approval_instance_id;

  const overheadTotal = sumSupplierInvoiceOverheadAmount(invoice);
  const poOverheads = getInvoicePoOverheads(invoice);
  const poOverheadTotal =
    invoice.po_overhead_total != null
      ? Number(invoice.po_overhead_total)
      : poOverheads.reduce((s, o) => s + (Number(o.amount ?? o.original_amount) || 0), 0);
  const unclearedPoOverhead = poOverheads.reduce((s, o) => {
    const total = Number(o.amount ?? o.original_amount) || 0;
    const cleared = Number(o.invoiced_amount) || 0;
    return s + Math.max(0, Math.round((total - cleared) * 100) / 100);
  }, 0);

  const lineColumns = [
    { title: '#', dataIndex: 'line_number', key: 'line_number', width: 50 },
    {
      title: t('item'),
      dataIndex: 'item_code',
      key: 'item_code',
      width: 140,
      render: (code: string, row: SupplierInvoiceLineDTO) => {
        const isOverhead = isSupplierInvoiceOverheadLine(row);
        return (
          <Space size={4} wrap>
            <ItemCodeWithCritical code={code} critical={isCriticalCode(row.item_code)} />
            {isOverhead ? <Tag color="orange">{t('overhead')}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: t('description'),
      dataIndex: 'item_description',
      key: 'item_description',
      render: (desc: string, row: SupplierInvoiceLineDTO) => {
        const isOverhead = isSupplierInvoiceOverheadLine(row);
        const text = formatItemDescriptionWithProfile(desc, row).trim() || '—';
        if (isOverhead) return /^overhead/i.test(text) ? text : t('overheadPrefix', { text });
        return (
          <>
            <div>{text}</div>
            <LineSpecificationsText value={row.specifications} />
          </>
        );
      },
    },
    {
      title: t('supplierDescription'),
      dataIndex: 'supplier_description',
      key: 'supplier_description',
      render: (v: string, row: SupplierInvoiceLineDTO) => v || row.item_description || '—',
    },
    { title: t('project'), key: 'project', width: 120, render: (_: unknown, row: SupplierInvoiceLineDTO) => projectLabel(row) },
    {
      title: t('wbs'),
      key: 'wbs',
      width: 140,
      ellipsis: true,
      render: (_: unknown, row: any) => (
        <BudgetCodeLabel
          projectId={row.project_id || (invoice as any).project_id}
          nodeId={row.wbs_node_id}
          kind="wbs"
        />
      ),
    },
    {
      title: t('cat'),
      dataIndex: 'cost_category',
      key: 'cost_category',
      width: 90,
      render: (v: string) => v || '—',
    },
    { title: t('qty'), dataIndex: 'quantity_billed', key: 'quantity_billed', width: 80, align: 'right' as const },
    { title: t('rate'), dataIndex: 'unit_price', key: 'unit_price', width: 100, align: 'right' as const, render: (v: number) => Number(v).toFixed(2) },
    { title: t('amount'), dataIndex: 'amount', key: 'amount', width: 110, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
  ];

  const canAlignTotal =
    invoice.invoice_status === 'Approved' &&
    !invoice.gl_posted &&
    Math.abs(totalAlignment.diff) >= 0.01 &&
    Math.abs(totalAlignment.diff) <= 0.05;

  const poNavId = (invoice.purchaseOrder as { id?: string } | undefined)?.id ?? (invoice as SupplierInvoiceDTO & { po_id?: string }).po_id;
  const grnNavId = (invoice.goodsReceiptNote as { id?: string } | undefined)?.id ?? (invoice as SupplierInvoiceDTO & { grn_id?: string }).grn_id;

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/supplier-invoices')}>
        {tc('back')}
      </Button>
      {['Draft', 'Submitted'].includes(invoice.invoice_status) && (
        <Button type="default" icon={<EditOutlined />} onClick={() => navigate(`/scm/procurement/supplier-invoices/${id}/edit`)}>
          {tc('edit')}
        </Button>
      )}
      {invoice.invoice_status === 'Draft' && (
        <Button loading={actionLoading === 'submit'} onClick={() => runAction('submit', () => supplierInvoiceService.submit(id!))}>
          {tc('submit')}
        </Button>
      )}
      {['Draft', 'Submitted'].includes(invoice.invoice_status) && (
        <Button onClick={() => void openExceedanceFromInvoice()}>{t('requestBudgetExceedance')}</Button>
      )}
      {invoice.invoice_status === 'Draft' && (
        <Button icon={<LinkOutlined />} loading={actionLoading === 'portal-link'} onClick={copySupplierPortalLink}>
          {t('copySupplierPortalLink')}
        </Button>
      )}
      {invoice.invoice_status === 'Submitted' && !showUniversalApproval && (
        <Button loading={actionLoading === 'approve'} onClick={() => runAction('approve', () => supplierInvoiceService.approve(id!))}>
          {tc('approve')}
        </Button>
      )}
      {invoice.invoice_status === 'Approved' && (
        <Button
          type="primary"
          loading={actionLoading === 'post'}
          onClick={() =>
            needsDiscountAccount
              ? setDiscountPostOpen(true)
              : runAction('post', () => supplierInvoiceService.post(id!))
          }
        >
          {t('postToGl')}
        </Button>
      )}
      {invoice.gl_posted && Number(invoice.paid_amount ?? 0) === 0 && (
        <Popconfirm
          title={t('unpostConfirmTitle')}
          description={t('unpostConfirmDesc')}
          onConfirm={() => runAction('unpost', () => supplierInvoiceService.unpost(id!))}
          okText={t('unpost')}
          cancelText={tc('cancel')}
        >
          <Button loading={actionLoading === 'unpost'}>{t('unpost')}</Button>
        </Popconfirm>
      )}
      {!['Posted', 'Cancelled'].includes(invoice.invoice_status) && (
        <Button danger loading={actionLoading === 'cancel'} onClick={() => runAction('cancel', () => supplierInvoiceService.cancel(id!))}>
          {tc('cancel')}
        </Button>
      )}
      <Button icon={<PrinterOutlined />} onClick={handlePrint}>
        {t('print')}
      </Button>
      <ReportDocumentActions
        reportKey="supplier-invoice"
        params={{ id: invoice.id }}
        documentId={invoice.id}
        sendPermissions={['procurement.invoice.create']}
        emailLabel={t("emailSupplier")}
      />
      {invoice.gl_posted && (
        <Button icon={<FileTextOutlined />} onClick={handlePrintVoucher}>
          {t('printVoucher')}
        </Button>
      )}
      {invoice.gl_posted && invoice.invoice_status !== 'Cancelled' && (
        <>
          <Button
            icon={<QrcodeOutlined />}
            loading={uaeEinvoiceLoading}
            onClick={async () => {
              if (!id) return;
              setUaeEinvoiceLoading(true);
              try {
                await complianceService.generateUaeEInvoice(id, 'SupplierInvoice');
                message.success(t('uaeEinvoiceArchived'));
                refetchUaeTransmissions();
              } catch (e: any) {
                message.error(e?.response?.data?.message || e?.message || t('uaeEinvoiceFailed'));
              } finally {
                setUaeEinvoiceLoading(false);
              }
            }}
          >
            {t('generateUaeEInvoice')}
          </Button>
          {uaeQrPayload ? (
            <Button icon={<QrcodeOutlined />} onClick={() => setUaeQrModalVisible(true)}>
              {t('viewQr')}
            </Button>
          ) : null}
        </>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>{t('quickLinks')}</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/scm/procurement/supplier-invoices')}>
          {t('allSupplierInvoices')}
        </Button>
        <Title level={5} style={{ marginTop: 16 }}>
          {t('uaeEInvoice')}
        </Title>
        {latestUaeTransmission ? (
          <Descriptions column={1} size="small">
            <Descriptions.Item label={tc("status")}>
              <Tag color={latestUaeTransmission.status === 'Cleared' ? 'green' : 'default'}>
                {latestUaeTransmission.status}
              </Tag>
            </Descriptions.Item>
            {latestUaeTransmission.exchange_uuid && (
              <Descriptions.Item label={t("uuid")}>{latestUaeTransmission.exchange_uuid}</Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <Text type="secondary">{t('uaeEinvoiceGenerateHint')}</Text>
        )}
        {poNavId && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate(`/scm/procurement/purchase-orders/${poNavId}`)}>
            {t('purchaseOrder')}
          </Button>
        )}
        {grnNavId && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate(`/scm/procurement/grn/${grnNavId}`)}>
            {t('grn')}
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0' }}>
      {id ? <AiPageContextSync module="procurement" docType="SupplierInvoice" docId={id} /> : null}
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel={t("purchaseBill")}
            documentNumber={invoice.invoice_number}
            statusTag={
              <Space wrap>
                <Tag color={statusColors[invoice.invoice_status] ?? 'default'}>{statusLabel(invoice.invoice_status)}</Tag>
                <Tag color={matchColors[invoice.matching_status] ?? 'default'}>
                  {t('matchLabel', { status: matchStatusLabel(invoice.matching_status) })}
                </Tag>
                {invoice.gl_posted && <Tag color="green">{t('glPosted')}</Tag>}
              </Space>
            }
            subtitle={
              <Text type="secondary">{invoice.received_date ? dayjs(invoice.received_date).format('DD MMM YYYY') : ''}</Text>
            }
            facts={
              <Space wrap>
                <Text>
                  <strong>{t('supplierColon')}</strong> {invoice.supplier?.supplier_name ?? invoice.supplier_id ?? '—'}
                </Text>
                <Text>
                  <strong>{t('balanceDueColon')}</strong> {Number(invoice.balance_due ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={siviWorkflowStages} current={siviWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        {canAlignTotal && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t("invoiceTotalMismatch")}
            description={
              <Space direction="vertical" size={8}>
                <Text>
                  {t('invoiceTotalMismatchDesc', {
                    stored: totalAlignment.stored.toFixed(2),
                    lineNet: totalAlignment.lineNet.toFixed(2),
                    discount: totalAlignment.discount.toFixed(2),
                    vat: totalAlignment.vat.toFixed(2),
                    expected: totalAlignment.expected.toFixed(2),
                    diff: totalAlignment.diff.toFixed(2),
                  })}
                </Text>
                <Button
                  type="primary"
                  loading={actionLoading === 'align'}
                  onClick={() =>
                    runAction('align', () => supplierInvoiceService.alignTotal(id!))
                  }
                >
                  {t('alignInvoiceTotal')}
                </Button>
              </Space>
            }
          />
        )}
        {invoice.invoice_status === 'Approved' &&
          !invoice.gl_posted &&
          Math.abs(totalAlignment.diff) > 0.05 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t("invoiceTotalMismatch")}
              description={t('invoiceTotalMismatchTooLarge', { diff: totalAlignment.diff.toFixed(2) })}
            />
          )}
        <TaskRoutingCard
          sourceDocumentType="SupplierInvoice"
          sourceDocumentId={invoice.id}
          nextActionUserId={(invoice as { next_action_user_id?: string | null }).next_action_user_id}
          documentOwnerLabel={t("documentOwnerCreatedBy")}
          documentOwnerUserId={(invoice as { created_by?: string | null }).created_by}
          disabled={invoice.invoice_status === 'Cancelled' || invoice.invoice_status === 'Posted'}
          invalidateQueryKeys={[['supplier-invoice', id!]]}
        />
        {showUniversalApproval && (
          <div style={{ marginBottom: 16 }}>
            <ApprovalPanel
              entityType={ENTITY_TYPE_SUPPLIER_INVOICE}
              entityId={invoice.id}
              onApproveViaDocument={async (remarks) => {
                await supplierInvoiceService.approve(id!, remarks);
              }}
              onRejectViaDocument={async (payload) => {
                await supplierInvoiceService.rejectApproval(id!, payload);
              }}
              extraInvalidateQueryKeys={[['supplier-invoice', id], ['supplier-invoices']]}
            />
          </div>
        )}
        <Card
          title={t("threeWayMatchingTitle")}
          style={{ marginBottom: 16 }}
          extra={
            <Space wrap>
              <Button
                size="small"
                type="link"
                onClick={() => navigate('/scm/procurement/three-way-matching')}
              >
                {t('matchingQueue')}
              </Button>
              {canMatch && (
                <Button
                  size="small"
                  loading={actionLoading === 'match'}
                  onClick={async () => {
                    setActionLoading('match');
                    try {
                      await supplierInvoiceService.runMatch(id!);
                      message.success(t('matchRecomputed'));
                      queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
                      refetchMatch();
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || e?.message || t('matchFailed'));
                    } finally {
                      setActionLoading(null);
                    }
                  }}
                >
                  {t('runMatch')}
                </Button>
              )}
              {canHold && invoice.matching_status !== 'Hold' && (
                <Button
                  size="small"
                  loading={actionLoading === 'hold'}
                  onClick={async () => {
                    setActionLoading('hold');
                    try {
                      await supplierInvoiceService.holdMatch(id!, matchNotes || undefined);
                      message.success(t('invoiceHeld'));
                      queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
                      refetchMatch();
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || e?.message || t('holdFailed'));
                    } finally {
                      setActionLoading(null);
                    }
                  }}
                >
                  {t('hold')}
                </Button>
              )}
              {canMatch && invoice.matching_status === 'Hold' && (
                <Button
                  size="small"
                  loading={actionLoading === 'release-hold'}
                  onClick={async () => {
                    setActionLoading('release-hold');
                    try {
                      await supplierInvoiceService.releaseMatchHold(id!);
                      message.success(t('holdReleased'));
                      queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
                      refetchMatch();
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || e?.message || t('releaseFailed'));
                    } finally {
                      setActionLoading(null);
                    }
                  }}
                >
                  {t('releaseHold')}
                </Button>
              )}
              {canMatch && (
                <Button size="small" onClick={() => setForceMatchOpen(true)}>
                  {t('forceMatch')}
                </Button>
              )}
            </Space>
          }
        >
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color={matchColors[invoice.matching_status] ?? 'default'}>
              {matchStatusLabel(invoice.matching_status)}
            </Tag>
            <Text>
              {t('varianceColon')}{' '}
              {Number(invoice.variance_amount ?? matchResult?.variance_amount ?? 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </Text>
            {(invoice.variance_notes || matchResult?.variance_notes) && (
              <Text type="secondary">{invoice.variance_notes || matchResult?.variance_notes}</Text>
            )}
          </Space>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            {t('paymentBlockedUntilMatched')}
          </Text>
          <Table
            size="small"
            rowKey="line_id"
            pagination={false}
            dataSource={(matchResult as ThreeWayMatchResultDTO | undefined)?.lines || []}
            rowClassName={(row) =>
              getCriticalItemRowClassName({ is_critical: isCriticalCode(row.item_code) })
            }
            columns={[
              { title: '#', dataIndex: 'line_number', width: 50 },
              {
                title: t('item'),
                dataIndex: 'item_code',
                width: 140,
                render: (code: string, row) => (
                  <ItemCodeWithCritical code={code} critical={isCriticalCode(row.item_code)} />
                ),
              },
              {
                title: t('invQty'),
                dataIndex: 'invoice_qty',
                align: 'right' as const,
                width: 80,
                render: (v: number) => Number(v).toLocaleString(),
              },
              {
                title: t('grnAccepted'),
                dataIndex: 'grn_accepted_qty',
                align: 'right' as const,
                width: 100,
                render: (v: number | null) => (v == null ? '—' : Number(v).toLocaleString()),
              },
              {
                title: t('invPrice'),
                dataIndex: 'invoice_unit_price',
                align: 'right' as const,
                width: 90,
                render: (v: number) => Number(v).toFixed(2),
              },
              {
                title: t('poPrice'),
                dataIndex: 'po_unit_price',
                align: 'right' as const,
                width: 90,
                render: (v: number | null) => (v == null ? '—' : Number(v).toFixed(2)),
              },
              {
                title: t('deltaAmount'),
                dataIndex: 'amount_delta',
                align: 'right' as const,
                width: 90,
                render: (v: number) => Number(v).toFixed(2),
              },
              {
                title: t('ok'),
                dataIndex: 'passed',
                width: 60,
                render: (v: boolean) => (v ? <Tag color="green">{tc('yes')}</Tag> : <Tag color="orange">{tc('no')}</Tag>),
              },
              {
                title: t('issues'),
                dataIndex: 'issues',
                ellipsis: true,
                render: (v: string[]) => (v?.length ? v.join(', ') : '—'),
              },
            ]}
          />
        </Card>
        {(poOverheads.length > 0 || overheadTotal > 0) && (
          <Card
            size="small"
            title={t("poOverheads")}
            style={{ marginBottom: 16 }}
            extra={
              unclearedPoOverhead > 0 && ['Draft', 'Submitted'].includes(invoice.invoice_status) ? (
                <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-invoices/${id}/edit`)}>
                  {t('clearOnBill')}
                </Button>
              ) : null
            }
          >
            {unclearedPoOverhead > 0 && overheadTotal <= 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={t("poOverheadsNotOnBill")}
                description={t('poOverheadsNotOnBillDesc', {
                  amount: unclearedPoOverhead.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                  currency: invoice.currency || '',
                })}
              />
            ) : null}
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => String(r.id)}
              dataSource={poOverheads}
              columns={[
                { title: t('type'), dataIndex: 'overhead_type', width: 140 },
                {
                  title: t('description'),
                  dataIndex: 'description',
                  render: (v: string | null | undefined, r) => v || r.remarks || '—',
                },
                {
                  title: t('amount'),
                  key: 'amount',
                  align: 'right' as const,
                  width: 110,
                  render: (_: unknown, r) =>
                    Number(r.amount ?? r.original_amount ?? 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    }),
                },
                {
                  title: t('cleared'),
                  key: 'cleared',
                  align: 'right' as const,
                  width: 110,
                  render: (_: unknown, r) =>
                    Number(r.invoiced_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 }),
                },
                {
                  title: t('remaining'),
                  key: 'remaining',
                  align: 'right' as const,
                  width: 110,
                  render: (_: unknown, r) => {
                    const total = Number(r.amount ?? r.original_amount ?? 0);
                    const cleared = Number(r.invoiced_amount ?? 0);
                    return Math.round((total - cleared) * 100) / 100;
                  },
                },
                {
                  title: t('onThisBill'),
                  key: 'on_bill',
                  width: 110,
                  render: (_: unknown, r) => {
                    const onBill = (invoice.lines ?? [])
                      .filter((l) => l.po_overhead_id === r.id)
                      .reduce((s, l) => s + (Number(l.amount) || 0), 0);
                    return onBill > 0 ? (
                      <Tag color="orange">{onBill.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Tag>
                    ) : (
                      <Tag>{t('notOnBill')}</Tag>
                    );
                  },
                },
              ]}
            />
          </Card>
        )}
        <Collapse
          defaultActiveKey={['header', 'lines']}
          items={[
            {
              key: 'header',
              label: t('billSettlementReferences'),
              children: (
                <div>
                  <Card bordered={false} styles={{ body: { padding: 0 } }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
          <Descriptions.Item label={t("supplier")}>{invoice.supplier?.supplier_name ?? invoice.supplier_id}</Descriptions.Item>
          <Descriptions.Item label={t("project")}>{headerProjectLabel(invoice as any, invoice.lines)}</Descriptions.Item>
          <Descriptions.Item label={t("department")}>
            {invoice.department_id
              ? (() => {
                  const d = departments.find((x) => x.id === invoice.department_id);
                  return d ? `${d.department_code} - ${d.department_name}` : invoice.department_id;
                })()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t("supplierInvoiceNo")}>{invoice.supplier_invoice_number}</Descriptions.Item>
          <Descriptions.Item label={t("invoiceDate")}>{invoice.supplier_invoice_date ? dayjs(invoice.supplier_invoice_date).format('DD-MMM-YYYY') : '—'}</Descriptions.Item>
          <Descriptions.Item label={t("billDate")}>{invoice.received_date ? dayjs(invoice.received_date).format('DD-MMM-YYYY') : '—'}</Descriptions.Item>
          <Descriptions.Item label={t("dueDate")}>{invoice.due_date ? dayjs(invoice.due_date).format('DD-MMM-YYYY') : '—'}</Descriptions.Item>
          <Descriptions.Item label={t("apCreditOnPost")}>
            {invoice.debitAccount ? `${invoice.debitAccount.account_code} – ${invoice.debitAccount.account_name}` : firstNonUuidDisplay(null)}
          </Descriptions.Item>
          <Descriptions.Item label={t("purchaseOrderShort")}>
            <DocumentRefLink
              to={invoice.po_id ? `/scm/procurement/purchase-orders/${invoice.po_id}` : undefined}
              number={invoice.purchaseOrder?.po_number}
              id={invoice.po_id}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t("grn")}>
            <DocumentRefLink
              to={invoice.grn_id ? `/scm/procurement/grn/${invoice.grn_id}` : undefined}
              number={invoice.goodsReceiptNote?.grn_number}
              id={invoice.grn_id}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t("subtotal")}>{Number(invoice.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          {overheadTotal > 0 ? (
            <Descriptions.Item label={t("overheadsOnThisBill")}>
              {overheadTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </Descriptions.Item>
          ) : null}
          {poOverheadTotal > 0 ? (
            <Descriptions.Item label={t("poOverheadTotal")}>
              {poOverheadTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              {unclearedPoOverhead > 0 && overheadTotal <= 0
                ? t('unclearedAmount', {
                    amount: unclearedPoOverhead.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                  })
                : ''}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label={t("discount")}>{Number(invoice.discount_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          {Number(invoice.discount_amount) > 0 && invoice.invoice_type !== 'ServiceReceiptMatched' ? (
            <Descriptions.Item label={t("discountAccount")}>
              {invoice.discountAccount
                ? `${invoice.discountAccount.account_code} – ${invoice.discountAccount.account_name}`
                : t('discountAccountNotSet')}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label={t("vat")}>{Number(invoice.vat_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          <Descriptions.Item label={t("charges")}>{Number(Number(invoice.freight_charges) + Number(invoice.other_charges)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          <Descriptions.Item label={t("grandTotal")}>{Number(invoice.grand_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          <Descriptions.Item label={t("paidAmount")}>{Number(invoice.paid_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          <Descriptions.Item label={t("debitNoteAdjusted")}>{Number(invoice.debit_note_adjusted_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          <Descriptions.Item label={t("advanceAdjusted")}>{Number(invoice.advance_adjusted_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          <Descriptions.Item label={t("balanceDue")}>{Number(invoice.balance_due ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
          {invoice.portal_submitted_at && (
            <Descriptions.Item label={t("supplierPortalSubmitted")}>
              {dayjs(invoice.portal_submitted_at).format('DD-MMM-YYYY HH:mm')}
            </Descriptions.Item>
          )}
          {invoice.notes && <Descriptions.Item label={t("notes")} span={3}>{invoice.notes}</Descriptions.Item>}
        </Descriptions>

        {allocations.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }}>
              <strong>{t('appliedDebitNotes')}</strong>
              {invoice.invoice_status !== 'Cancelled' && invoice.supplier_id && (
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setApplyDnModalOpen(true)}>
                  {t('applyDebitNotes')}
                </Button>
              )}
            </Space>
            <Table
              size="small"
              dataSource={allocations.map((a) => ({ ...a, key: a.id }))}
              columns={[
                { title: t('debitNote'), dataIndex: ['supplierDebitNote', 'debit_note_number'], key: 'dn_number', width: 140 },
                { title: t('date'), dataIndex: 'adjustment_date', key: 'adj_date', width: 120, render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '—' },
                { title: t('amount'), dataIndex: 'allocated_amount', key: 'amount', width: 120, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
              ]}
              pagination={false}
            />
          </div>
        ) : invoice.invoice_status !== 'Cancelled' && invoice.supplier_id ? (
          <div style={{ marginTop: 16 }}>
            <Button type="default" icon={<PlusOutlined />} onClick={() => setApplyDnModalOpen(true)}>
              {t('applyDebitNotes')}
            </Button>
          </div>
        ) : null}

        {advanceAllocations.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }}>
              <strong>{t('appliedSupplierAdvances')}</strong>
              {invoice.invoice_status !== 'Cancelled' && invoice.supplier_id && (
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setApplyAdvanceModalOpen(true)}>
                  {t('applySupplierAdvances')}
                </Button>
              )}
            </Space>
            <Table
              size="small"
              dataSource={advanceAllocations.map((a) => ({ ...a, key: a.id }))}
              columns={[
                { title: t('voucherNo'), dataIndex: ['paymentVoucher', 'voucher_number'], key: 'pv_number', width: 140 },
                { title: t('date'), dataIndex: 'adjustment_date', key: 'adj_date', width: 120, render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '—' },
                { title: t('amount'), dataIndex: 'allocated_amount', key: 'amount', width: 120, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
              ]}
              pagination={false}
            />
          </div>
        ) : invoice.invoice_status !== 'Cancelled' && invoice.supplier_id && !isGlPosted ? (
          <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }}>
              <strong>{t('supplierAdvancesToApplyOnPosting')}</strong>
              <Button size="small" icon={<EditOutlined />} onClick={() => setApplyAdvanceModalOpen(true)}>
                {plannedAdvances.length ? t('change') : (openAdvances.length ? t('selectAdvancesWithOpen', { count: openAdvances.length }) : t('selectAdvances'))}
              </Button>
            </Space>
            {plannedAdvances.length > 0 ? (
              <Table
                size="small"
                rowKey="payment_voucher_id"
                dataSource={plannedAdvances}
                columns={[
                  { title: t('voucherNo'), dataIndex: 'voucher_number', key: 'pv_number', width: 140 },
                  { title: t('date'), dataIndex: 'voucher_date', key: 'date', width: 120, render: (v: string) => (v ? dayjs(v).format('DD-MMM-YYYY') : '—') },
                  { title: t('toApply'), dataIndex: 'planned_amount', key: 'planned', width: 120, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                  { title: t('openNow'), dataIndex: 'open_balance', key: 'open', width: 120, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                ]}
                pagination={false}
              />
            ) : null}
          </div>
        ) : invoice.invoice_status !== 'Cancelled' && invoice.supplier_id ? (
          <div style={{ marginTop: 16 }}>
            <Button type="default" icon={<PlusOutlined />} onClick={() => setApplyAdvanceModalOpen(true)}>
              {t('applySupplierAdvances')}
            </Button>
          </div>
        ) : null}
                  </Card>
                </div>
              ),
            },
            {
              key: 'lines',
              label: t('lineItems'),
              children: (
                <div>
                  {invoice.lines?.length ? (
                    <Table
                      size="small"
                      columns={lineColumns}
                      dataSource={(invoice.lines as SupplierInvoiceLineDTO[]).map((l) => ({ ...l, key: l.id }))}
                      rowClassName={(row: SupplierInvoiceLineDTO) =>
                        getCriticalItemRowClassName({ is_critical: isCriticalCode(row.item_code) })
                      }
                      pagination={false}
                    />
                  ) : (
                    <Text type="secondary">{t('noLineItems')}</Text>
                  )}
                </div>
              ),
            },
          ]}
        />
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_INVOICE}
          entityId={id}
          readOnly={invoice.invoice_status === 'Cancelled' || invoice.invoice_status === 'Posted'}
          queryKeyPrefix={['documents', 'supplier-invoice']}
        />
      </WorkspaceLayout>

      <Modal
        title={t("applyDebitNotesToInvoice")}
        open={applyDnModalOpen}
        onCancel={() => setApplyDnModalOpen(false)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <p style={{ marginBottom: 16 }}>
          {t('netPayableBeforeAdjustment', {
            amount: netPayableBeforeDn.toLocaleString(undefined, { minimumFractionDigits: 2 }),
          })}
        </p>
        <Form
          layout="vertical"
          onFinish={handleApplyDebitNotes}
          initialValues={{ amounts: {} }}
        >
          {openDebitNotes.length === 0 ? (
            <p>{t('noOpenDebitNotes')}</p>
          ) : (
            <>
              <Table
                size="small"
                dataSource={openDebitNotes.map((dn) => ({ ...dn, key: dn.id }))}
                columns={[
                  { title: t('debitNote'), dataIndex: 'debit_note_number', key: 'dn_number', width: 120 },
                  { title: t('date'), dataIndex: 'debit_note_date', key: 'date', width: 100 },
                  { title: t('total'), dataIndex: 'total_amount', key: 'total', width: 90, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                  { title: t('openBalance'), dataIndex: 'open_balance', key: 'open', width: 100, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                  {
                    title: t('amountToApply'),
                    key: 'amount',
                    width: 140,
                    render: (_: unknown, row: OpenDebitNoteRow) => (
                      <Form.Item name={['amounts', row.id]} noStyle rules={[{ required: false }]}>
                        <InputNumber
                          min={0}
                          max={row.open_balance}
                          step={0.01}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="0"
                        />
                      </Form.Item>
                    ),
                  },
                ]}
                pagination={false}
              />
              <Form.Item style={{ marginTop: 16 }}>
                <Space>
                  <Button type="primary" htmlType="submit" loading={applyDnSubmitting}>
                    {t('apply')}
                  </Button>
                  <Button onClick={() => setApplyDnModalOpen(false)}>{tc('cancel')}</Button>
                </Space>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={isGlPosted ? t('applySupplierAdvancesToInvoice') : t('supplierAdvancesToApplyOnPosting')}
        open={applyAdvanceModalOpen}
        onCancel={() => setApplyAdvanceModalOpen(false)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <p style={{ marginBottom: 16 }}>
          {t('remainingPayable', {
            amount: Number(invoice?.balance_due ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 }),
          })}{' '}
          {isGlPosted ? t('selectAdvancesToApply') : t('advancesKnockOffOnPost')}
        </p>
        <Form
          layout="vertical"
          onFinish={handleApplyAdvances}
          initialValues={{
            amounts: isGlPosted
              ? {}
              : Object.fromEntries(plannedAdvances.map((p) => [p.payment_voucher_id, p.planned_amount])),
          }}
        >
          {openAdvances.length === 0 ? (
            <p>{t('noOpenSupplierAdvances')}</p>
          ) : (
            <>
              <Table
                size="small"
                dataSource={openAdvances.map((pv) => ({ ...pv, key: pv.id }))}
                columns={[
                  { title: t('voucherNo'), dataIndex: 'voucher_number', key: 'voucher_number', width: 120 },
                  { title: t('date'), dataIndex: 'voucher_date', key: 'date', width: 100 },
                  { title: t('amount'), dataIndex: 'amount', key: 'total', width: 90, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                  { title: t('openBalance'), dataIndex: 'open_balance', key: 'open', width: 100, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                  {
                    title: t('amountToApply'),
                    key: 'amount',
                    width: 140,
                    render: (_: unknown, row: OpenAdvanceRow) => (
                      <Form.Item name={['amounts', row.id]} noStyle rules={[{ required: false }]}>
                        <InputNumber
                          min={0}
                          max={row.open_balance}
                          step={0.01}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="0"
                        />
                      </Form.Item>
                    ),
                  },
                ]}
                pagination={false}
              />
              <Form.Item style={{ marginTop: 16 }}>
                <Space>
                  <Button type="primary" htmlType="submit" loading={applyAdvanceSubmitting}>
                    {isGlPosted ? t('apply') : tc('save')}
                  </Button>
                  <Button onClick={() => setApplyAdvanceModalOpen(false)}>{tc('cancel')}</Button>
                </Space>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={t("uaeEInvoiceQr")}
        open={uaeQrModalVisible}
        onCancel={() => setUaeQrModalVisible(false)}
        footer={null}
        centered
      >
        {uaeQrPayload ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <QRCode value={uaeQrPayload} size={200} level="H" />
          </div>
        ) : null}
      </Modal>

      <Modal
        title={t("discountAccount")}
        open={discountPostOpen}
        onCancel={() => setDiscountPostOpen(false)}
        okText={t("postToGl")}
        okButtonProps={{ disabled: !discountPostAccountId }}
        confirmLoading={actionLoading === 'post'}
        onOk={() =>
          runAction('post', async () => {
            await supplierInvoiceService.post(id!, { discount_account_id: discountPostAccountId });
            setDiscountPostOpen(false);
          })
        }
      >
        <Text type="secondary">
          {t('discountPostHint', {
            amount: formatMoney(invoice.discount_amount, PRINT_PLAIN_AMOUNT_PROFILE),
          })}
        </Text>
        <Select
          style={{ width: '100%', marginTop: 12 }}
          placeholder={t('selectDiscountAccount')}
          options={discountAccountOptions}
          value={discountPostAccountId}
          onChange={setDiscountPostAccountId}
          showSearch
          optionFilterProp="label"
        />
      </Modal>

      <Modal
        title={t("forceMatchInvoice")}
        open={forceMatchOpen}
        onCancel={() => setForceMatchOpen(false)}
        onOk={async () => {
          setActionLoading('force-match');
          try {
            await supplierInvoiceService.forceMatch(id!, matchNotes || undefined);
            message.success(t('invoiceForceMatched'));
            setForceMatchOpen(false);
            setMatchNotes('');
            queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
            refetchMatch();
          } catch (e: any) {
            message.error(e?.response?.data?.message || e?.message || t('forceMatchFailed'));
          } finally {
            setActionLoading(null);
          }
        }}
        confirmLoading={actionLoading === 'force-match'}
        okText={t("forceMatch")}
      >
        <Text type="secondary">
          {t('forceMatchHint')}
        </Text>
        <Input.TextArea
          style={{ marginTop: 12 }}
          rows={3}
          placeholder={t('forceMatchReasonPlaceholder')}
          value={matchNotes}
          onChange={(e) => setMatchNotes(e.target.value)}
        />
      </Modal>

      <BudgetExceedanceRequestModal
        open={exceedanceOpen}
        onClose={() => setExceedanceOpen(false)}
        projectId={exceedanceProjectId}
        sourceEntityType="supplier_invoice"
        sourceEntityId={id!}
        lines={exceedanceLines}
        checkResult={exceedanceCheck as any}
        onSubmitted={() => {
          queryClient.invalidateQueries({ queryKey: ['supplier-invoice', id] });
        }}
      />
    </div>
  );
};

export default SupplierInvoiceDetailPage;
